<template>
  <div>
    <PageAllow controller="Purchases" action="short" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherStock v-model="stockSelected" unclearable :stockTypes="[0]" />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="$permission.permissionAllow('Purchases', 'add')"
              small
              color="primary"
              class="elevation-1 ms-1"
              width="110px"
              :disabled="!showSelected.length"
              @click="submit()"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("PurchasesList.Fields.add") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="key_word"
                  v-model="options.keyword"
                  @text="materialKeyword = $event"
                  :label="$t('Stockouts.seacherMaterialKeyword')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterialCategory v-model="categorySelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherSupplier v-model="supplierSelected" />
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.current_count_status"
                  :items="$consts.currentStocks.currentCountStatus"
                  :label="$t('CurrentStocks.currentCountStatus.title')"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 my-4">
        <v-data-table
          v-model="showSelected"
          id="stockouts_table"
          :headers="headers"
          class="elevation-2 pa-4 mt-4"
          :items="stockMaterials"
          show-select
          item-key="id"
          disable-sort
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :footer-props="$t('$config.footer-props')"
          :no-data-text="$t('Stockouts.DetailNoData')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.material_name="{ item }">
            {{ item.material_name }}
            <br />
            <span v-if="item.standard_name" class="caption secondary--text">
              {{ $t("Material.Fields.standard") }}{{ $t("Global.colon")}}
              <span v-if="item.standard_name && item.standard_name.length > 12" :title="item.standard_name" style="cursor: pointer">
                {{ item.standard_name.slice(0, 12) + "......" }}
              </span>
              <span v-else>
                {{ item.standard_name }}
              </span>
            </span>
          </template>
          <template v-slot:item.is_lack="{ item }">
            <v-chip
              small
              label
              :color="item.is_lack ? 'danger' : 'primary'"
              text-color="white"
            >
              {{
                item.is_lack
                  ? $t("Stockouts.stockoutFlgs.on")
                  : $t("Stockouts.stockoutFlgs.off")
              }}
            </v-chip>
            <br />
            <span
              v-if="Number(item.purchase_qty) > 0"
              class="pt-n1 text-no-wrap caption orange--text darken-3"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ $t("Stockouts.replenishment") }}
            </span>
          </template>
          <template v-slot:item.stock_unit_short_num="{ item }">
            <span v-if="zhHans">
              {{ item.stock_unit_short_num }}
            </span>
            <span v-else>
              {{ item.stock_single_unit_short_num }}
              <br />
              <span class="pt-n1 text-no-wrap caption">
                {{ item.standard_detail.unit }}
              </span>
            </span>
          </template>
          <template v-slot:item.stock_unit_purchase_qty="{ item }">
            <span v-if="zhHans">
              {{ item.stock_unit_purchase_qty }}
            </span>
            <span v-else>
              {{ item.stock_single_unit_purchase_qty }}
              <br />
              <span class="pt-n1 text-no-wrap caption">
                {{ item.standard_detail.unit }}
              </span>
            </span>
          </template>
          <template v-slot:item.stock_unit_num="{ item }">
            <span v-if="zhHans">
              {{ item.stock_unit_num }}
            </span>
            <span v-else>
              {{ item.stock_single_unit_num }}
              <br />
              <span class="pt-n1 text-no-wrap caption">
                {{ item.standard_detail.unit }}
              </span>
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <template>
              <v-btn
                small
                color="info"
                class="px-2 me-2 mb-1"
                :href="'/#/current-stocks/' + item.material_id"
              >
                {{ $t("Stockouts.Actions.inventoryDetail") }}
              </v-btn>
            </template>
            <template>
              <v-btn
                small
                color="info"
                class="px-2 me-2 mb-1"
                :href="
                  '/#/current-stocks?stock_id=' +
                  item.stock_id +
                  '&material_id=' +
                  item.material_id
                "
              >
                {{ $t("Stockouts.Actions.inAndOutDetail") }}
              </v-btn>
            </template>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherStock from "@/components/plugins/SearcherStock";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherMaterialCategory from "@/components/plugins/SearcherMaterialCategory";
import SearcherSupplier from "@/components/plugins/SearcherSupplier";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import Purchases from "@/api/purchases";

const defaultOptions = {
  keyword: "",
  category_codes: "",
  stock_id: "",
  supplier_id: "",
  current_count_status: []
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherStock,
    SearcherKeyword,
    SearcherMaterialCategory,
    SearcherSupplier,
    SearcherStatus,
  },

  computed: {
    zhHans() {
      return this.$i18n.locale == "zhHans";
    },

    items() {
      var items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.purchases"),
          disabled: false,
          href: "/purchases",
        },
        {
          text: i18n.t("Nav.stockout"),
          disabled: true,
        },
      ];

      return items;
    },

    headers() {
      var headers = [
        {
          text: i18n.t("Stockouts.Fields.material_category"),
          align: "start",
          value: "category_name",
        },
        {
          text: i18n.t("Stockouts.Fields.material_code"),
          align: "start",
          value: "material_code",
        },
        {
          text: i18n.t("Stockouts.Fields.material_name"),
          align: "start",
          value: "material_name",
        },
        {
          text: i18n.t("Stockouts.Fields.stockout_status"),
          align: "center",
          value: "is_lack",
        },
        {
          text: i18n.t("Stockouts.Fields.short_num"),
          align: "end",
          value: "stock_unit_short_num",
        },
        // {
        //   text: i18n.t("Stockouts.Fields.default_short_num"),
        //   align: "end",
        //   value: "short_num",
        // },
        {
          text: i18n.t("Stockouts.Fields.purchase_num"),
          align: "end",
          value: "stock_unit_purchase_qty",
        },
        // {
        //   text: i18n.t("Stockouts.Fields.default_purchase_num"),
        //   align: "end",
        //   value: "purchase_qty",
        // },
        {
          text: i18n.t("Stockouts.Fields.current_stock_num"),
          align: "end",
          value: "stock_unit_num",
        },
        // {
        //   text: i18n.t("Stockouts.Fields.default_current_stock_num"),
        //   align: "end",
        //   value: "current_count",
        // },
        // {
        //   text: i18n.t("TableHeaders.actions"),
        //   align: "start",
        //   sortable: false,
        //   value: "actions",
        // },
      ];

      return headers;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["settings"]),
  },

  data: () => ({
    panels: [0],
    filter: "",
    mode: "",
    paging: {},
    stockMaterials: [],
    stockout_material_count: 0,
    unfinished_purchase_count: 0,
    processing_purchase_count: 0,
    stockSelected: null,
    showSelected: [],
    submitable: false,
    categorySelected: null,
    supplierSelected: null,
    materialKeyword: "",
    options: JSON.parse(
      sessionStorage.getItem("stockouts-options")
        ? sessionStorage.getItem("stockouts-options")
        : JSON.stringify(defaultOptions)
    ),
  }),

  mounted() {
    this.$commonUtils
      .loadOptions("stockouts", [
        "stockSelected",
        "categorySelected",
        "supplierSelected",
      ])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "stockSelected":
                this.stockSelected = selected.value;
                break;
              case "categorySelected":
                this.categorySelected = selected.value;
                break;
              case "supplierSelected":
                this.supplierSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    resetSearch() {
      this.stockSelected = null;
      this.categorySelected = null;
      this.supplierSelected = null;
      this.materialKeyword = "";
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.materialKeyword,
      });
    },
    getStockMaterialsFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      if (options.stock_id)
        Purchases.stockoutFetch(
          Object.assign(options, { material_delete_flg: [0] })
        ).then(
          function (response) {
            if (response.data.status.isOK()) {
              this.stockMaterials = response.data.stock_materials;
              this.stockout_material_count = response.data.short_count;
              this.unfinished_purchase_count =
                response.data.purchase_uncomplete_count;
              this.processing_purchase_count =
                response.data.processing_purchase_count;
              this.paging = response.data.paging;
            }
          }.bind(this)
        );
    },
    submit() {
      if (this.showSelected.length) {
        var materialIds = this.showSelected.map((s) => {
          if (s.default_supplier_id) return s.material_id;
        });
        this.$router.push({
          path: "/purchases/new-from-products",
          query: {
            stock_id: this.stockSelected.id,
            material_ids: encodeURI(materialIds),
          },
        });
      }
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getStockMaterialsFromAPI();
        sessionStorage.setItem(
          "stockouts-options",
          JSON.stringify(this.options)
        );
      },
    },
    stockSelected: {
      deep: true,
      handler() {
        if (this.stockSelected && this.stockSelected.id) {
          this.options.stock_id = this.stockSelected.id;
        } else {
          this.options.stock_id = "";
        }
        sessionStorage.setItem(
          "stockouts-stockSelected",
          JSON.stringify(this.stockSelected)
        );
      },
    },
    categorySelected: {
      handler() {
        this.options.category_codes = this.categorySelected
          ? this.categorySelected.code
          : "";
        sessionStorage.setItem(
          "stockouts-categorySelected",
          JSON.stringify(this.categorySelected)
        );
      },
    },
    supplierSelected: {
      handler() {
        this.options.supplier_id = this.supplierSelected
          ? this.supplierSelected.id
          : "";
        sessionStorage.setItem(
          "stockouts-supplierSelected",
          JSON.stringify(this.supplierSelected)
        );
      },
    },
    // showSelected: {
    //   handler() {
    //     if (this.showSelected.find((s) => s.default_supplier_id))
    //       this.submitable = true;
    //     else this.submitable = false;
    //   },
    // },
  },
};
</script>